import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/styles";
import { 
    Card, 
    CardContent, 
    Typography, 
    Grid, 
    Chip, 
    IconButton,
    Collapse,
    Box,
    Tooltip
} from "@material-ui/core";
import { useSelector } from "react-redux";
import { formatDateTime, isStaffUser } from "utils";
import { CrudOptions, Lookup } from "frame/components";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
	root: {
		marginBottom: theme.spacing(1),
		borderRadius: theme.spacing(1),
		border: `0px solid rgba(0, 0, 0, 0.08)`,
		backgroundColor: theme.palette.background.paper,
		transition: "all 0.2s ease-in-out",
		"&:hover": {
			boxShadow: theme.shadows[1]
		}
	},
    content: {
        padding: theme.spacing(0.7, 1.25, 0.5, 1.25),
        "&:last-child": {
            paddingBottom: theme.spacing(0.5)
        }
    },
    collapsedContent: {
        display: "block",
        minHeight: "60px",
        paddingTop: theme.spacing(0.5),
        paddingBottom: theme.spacing(0.5),
        paddingLeft: theme.spacing(0.5),
        paddingRight: theme.spacing(0.5)
    },
    collapsedRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: theme.spacing(0.5),
        "&:last-child": {
            marginBottom: 0
        }
    },
    collapsedLeft: {
        minWidth: 0
    },
    collapsedRight: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1)
    },
	expandedContent: {
		paddingTop: theme.spacing(1)
	},
	leaveType: {
		fontWeight: 600,
		fontSize: "1rem",
		color: theme.palette.text.primary
	},
	staffName: {
		fontSize: "0.875rem",
		color: theme.palette.text.secondary,
		marginTop: theme.spacing(0.5)
	},
	timeInfo: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(0.5),
		fontSize: "0.875rem",
		fontWeight: 500
	},
	statusChip: {
		height: "24px",
		fontSize: "0.75rem"
	},
	active: {
		backgroundColor: theme.palette.success.main,
		color: theme.palette.white
	},
	closed: {
		backgroundColor: theme.palette.primary.main,
		color: theme.palette.white
	},
    expandButton: {
        padding: 0
    },
	detailsGrid: {
		marginTop: theme.spacing(1)
	},
    detailsTable: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: theme.spacing(0.5)
    },
    row: {
        display: "contents"
    },
    cell: {
        padding: theme.spacing(0.5)
    },
    singleCell: {
        gridColumn: "1 / -1",
        padding: theme.spacing(0.5)
    },
	detailItem: {
		marginBottom: theme.spacing(0.5)
	},
	detailLabel: {
		fontSize: "0.75rem",
		color: theme.palette.text.secondary,
		fontWeight: 500,
		marginBottom: theme.spacing(0.25)
	},
	detailValue: {
		fontSize: "1rem",
		color: theme.palette.text.primary,
		fontWeight: 500
	},
    actionsContainer: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1)
    },
    // Truncation helpers to mirror timesheet cards
    truncatedStaffName: {
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "nowrap",
        maxWidth: "180px"
    },
    truncatedLeaveType: {
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxHeight: "2.4em",
        lineHeight: "1.2em",
        maxWidth: "200px"
    }
}));

export const LeaveCardCollapsed = ({ leave, onDelete, onEdit, role, forceExpanded = false, hideActions = false }) => {
	const classes = useStyles();
	const [expanded, setExpanded] = useState(false);
	const leaveTypes = useSelector(({ leaveTypes }) => leaveTypes?.data || []);

	const leaveTypeId = leave?.leaveTypeID || leave?.leaveTypeId || leave?.siteId || "";
	const leaveTypeFromLookup =
		leaveTypes && leaveTypes.length
			? leaveTypes.find((lt) => String(lt?.id) === String(leaveTypeId))?.leaveType
			: null;
	const leaveTypeLabel = leaveTypeFromLookup || leave?.leaveType || "-";

	const toggleExpanded = () => {
		setExpanded(!expanded);
	};

    useEffect(() => {
        setExpanded(!!forceExpanded);
    }, [forceExpanded]);

	// Calculate duration
	const getDuration = () => {
		if (!leave.startDateTimeUTC) return "-";
		if (!leave.endDateTimeUTC) return "In Progress";
		
		const start = moment(leave.startDateTimeUTC);
		const end = moment(leave.endDateTimeUTC);
		const duration = moment.duration(end.diff(start));
		
		const totalMinutes = Math.round(duration.asMinutes());
		const days = Math.floor(totalMinutes / (60 * 24));

		// For multi-day ranges, keep the "Xd Yh" format
		if (days > 0) {
			const remainingMinutes = totalMinutes - days * 24 * 60;
			const remainingHours = Math.floor(remainingMinutes / 60);
			return `${days}d ${remainingHours}h`;
		}

		// Same-day: show decimal hours (e.g. 9.5h)
		const totalHours = totalMinutes / 60;
		const formatted =
			Math.abs(totalHours - Math.round(totalHours)) < 1e-9
				? `${Math.round(totalHours)}`
				: `${Number(totalHours.toFixed(1))}`; // one decimal place

		return `${formatted}h`;
	};

	return (
		<Card className={classes.root}>
			<CardContent className={classes.content}>
            {/* Collapsed View (aligned with timesheet card) */}
            <div className={classes.collapsedContent}>
                {/* Row 1: Staff name | Duration */}
                <div className={classes.collapsedRow}>
                    <div className={classes.collapsedLeft}>
                        <Tooltip title={<Lookup id={leave.staffId} resource="staff" attribute="fullName" />} placement="top">
                            <Typography className={`${classes.leaveType} ${classes.truncatedStaffName}`}>
                                <Lookup id={leave.staffId} resource="staff" attribute="fullName" />
                            </Typography>
                        </Tooltip>
                    </div>
                    <div className={classes.collapsedRight}>
                        <div className={classes.timeInfo}>
                            {getDuration()}
                        </div>
                    </div>
                </div>

                {/* Row 2: Leave Type | Expand */}
                <div className={classes.collapsedRow}>
                    <div className={classes.collapsedLeft}>
                        <Tooltip title={leaveTypeLabel} placement="top">
                            <Typography className={`${classes.staffName} ${classes.truncatedLeaveType}`}>
                                {leaveTypeLabel}
                            </Typography>
                        </Tooltip>
                    </div>
                    <div className={classes.collapsedRight}>
                        <IconButton 
                            className={classes.expandButton}
                            onClick={toggleExpanded}
                            size="small"
                            aria-label={expanded ? "Collapse" : "Expand"}
                        >
                            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </div>
                </div>
            </div>

				{/* Expanded View */}
				<Collapse in={expanded}>
					<div className={classes.expandedContent}>
                        <div className={classes.detailsTable}>
                            {/* Start */}
                            <div className={classes.singleCell}>
                                <Typography className={classes.detailLabel}>Start</Typography>
                                <Typography className={classes.detailValue}>
                                    {formatDateTime(leave.startDateTimeUTC)}
                                </Typography>
                            </div>

                            {/* End (on new line under Start) */}
                            <div className={classes.singleCell}>
                                <Typography className={classes.detailLabel}>End</Typography>
                                <Typography className={classes.detailValue}>
                                    {leave.endDateTimeUTC ? formatDateTime(leave.endDateTimeUTC) : "-"}
                                </Typography>
                            </div>

                            {/* Duration */}
                            <div className={classes.singleCell}>
                                <Typography className={classes.detailLabel}>Duration</Typography>
                                <Typography className={classes.detailValue}>
                                    {getDuration()}
                                </Typography>
                            </div>

                            {/* Row: Staff type | Phone */}
                            <div className={classes.row}>
                                <div className={classes.cell}>
                                    <Typography className={classes.detailLabel}>Staff Type</Typography>
                                    <Typography className={classes.detailValue}>
                                        {leave.organisationStaffType || "-"}
                                    </Typography>
                                </div>
                                <div className={classes.cell}>
                                    <Typography className={classes.detailLabel}>Staff Phone</Typography>
                                    <Typography className={classes.detailValue}>
                                        <Lookup id={leave.staffId} resource="staff" attribute="phoneNumber" />
                                    </Typography>
                                </div>
                            </div>

                            {/* Notes spanning both columns */}
                            <div className={classes.singleCell}>
                                <Typography className={classes.detailLabel}>Notes</Typography>
                                <Typography className={classes.detailValue}>
                                    {leave.notes || "-"}
                                </Typography>
                            </div>
                        </div>

                        {!isStaffUser(role) && !hideActions && (
							<Box mt={2} display="flex" justifyContent="flex-end" position="relative">
								<CrudOptions 
									position="bottom-right"
									onEdit={onEdit} 
									onDelete={!leave.isActive ? () => onDelete(leave.id) : null} 
								/>
							</Box>
						)}
					</div>
				</Collapse>
			</CardContent>
		</Card>
	);
};
